import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import ReactMarkdown from "react-markdown";
import { removeToken, setToken, setDealAndToken, commentBeautify } from "../shared/Helpers";
import getAccessToken from "../app/services/getAccessToken";
import getUser from "../app/services/getUser";
import { useLocation, useNavigate } from "react-router-dom";
import Circle from "../assets/img/check_circle.svg";
import Edit from "../assets/img/edit_comment.svg";
import Arrow from "../assets/img/next-arrow.svg";
import Loader from "../components/Loader";
import updateUser from "../app/services/updateUser";
import rotaGateway from "../app/services/rotaGateway";
import Cycle from "../assets/img/cycle.svg";
import rehypeRaw from "rehype-raw";

const ClientReviewLetter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dealAndToken = queryParams.get("customer");

  const [isLoading, setIsLoading] = useState(true);
  const [isLetterEdit, setLetterEdit] = useState(false);
  const [isLetterSubmitted, setLetterSubmitted] = useState(false);
  const [dealID, setDealID] = useState("");
  const [userDetails, setUserDetails] = useState({});
  const [charCount, setCharCount] = useState(0);
  const maxCharLimit = 5000;
  const [authToken, setAuthToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const handleError = (error, type, description) => {
    console.error(description, error);
    setError({ error, type, description });
  };

  useEffect(() => {
    const decryptAndFetchToken = async () => {
      if (dealAndToken) {
        try {
          const { data } = await getAccessToken(dealAndToken);
          const userDetails = await getUser(data.token);

          setAuthToken(data.token);
          setToken(data.token);
          setDealAndToken(dealAndToken);
          setDealID(userDetails.data.dealID);
          setUserDetails(userDetails.data);
          setLetterSubmitted(userDetails.data.wpLetterReview);

          // If the letter hasn't been checked, start polling
          if (!userDetails.data.checkedWpLetter) {
            const intervalId = setInterval(async () => {
              try {
                const { data } = await getAccessToken(dealAndToken);
                const updatedUserDetails = await getUser(data.token);
                setToken(data.token);
                setUserDetails(updatedUserDetails.data);

                // Stop polling if the letter is available
                if (updatedUserDetails.data.checkedWpLetter) {
                  clearInterval(intervalId);
                  setIsLoading(false);
                }
              } catch (error) {
                console.error("Error polling for the letter:", error);
              }
            }, 45000);

            // Clean up polling on component unmount
            return () => clearInterval(intervalId);
          } else {
            setIsLoading(false);
          }
        } catch (error) {
          handleError(error, 'fetch', "Error fetching access token or user details");
        }
      } else {
        removeToken();
        console.error("No dealAndToken provided in URL");
        navigate("/login");
      }
    };

    decryptAndFetchToken();
  }, [navigate, dealAndToken]);

  const edit = () => {
    setLetterEdit(true);
  };

  const approve = async () => {
    if (dealID) {
      setIsSubmitting(true);
      try {
        const baseUrl = window.location.origin;
        const payload = {
          dealID: dealID,
          wpLetterUrl: `${baseUrl}/internal-review?customer=${dealAndToken}`,
          stage: "trainee",
        };
        const updateField = {
          wpLetterReview: true,
        };
        await rotaGateway({
          method: 'POST',
          path: 'v1/submit-letter',
          body: payload,
        });
        await updateUser(authToken, updateField);
        navigate("/submitted-successfully");
      } catch (error) {
        handleError(error, 'submit', "Error submitting the letter (Approve action)");
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  const formik = useFormik({
    initialValues: {
      letterFeedback: "",
      clientAnswer: "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.letterFeedback && !userDetails.SentBackToClient) {
        errors.letterFeedback = "Required";
      }
      if (!values.clientAnswer && userDetails.SentBackToClient) {
        errors.clientAnswer = "Required";
      }
      return errors;
    },
    onSubmit: async (values) => {
      if (dealID) {
        setIsSubmitting(true);
        const formattedComments = Array.isArray(userDetails.comments) ? userDetails.comments.map(
                                        (comment) => {
                                          const date = comment.timestamp ? new Date(comment.timestamp).toLocaleDateString("en-GB") : "";
                                          return [
                                            "_________________",
                                            `${comment.type} (message sent on ${date}): ${comment.comment}`,
                                          ].join("\n");
                                        }).join("\n")
                                    : "";
        // NEW COMMENT (Client)
        const today = new Date().toLocaleDateString("en-GB");
        let newComment = ''
        if (values?.clientAnswer) {
          newComment = [
            "_________________",
            `Client (message sent on ${today}): ${values.clientAnswer}`,
          ].join("\n");
        } else if (
          values?.letterFeedback &&
          (!Array.isArray(userDetails.comments) || userDetails.comments.length === 0)
        ) {
          newComment = [
            "_________________",
            `Client (message sent on ${today}): ${values.letterFeedback}`,
          ].join("\n");
        }
        // FINAL TEXT
        const finalText = [formattedComments, newComment]
          .filter(Boolean)
          .join("\n");


        try {
          const baseUrl = window.location.origin;
          const payload = {
            dealAndToken,
            dealID: dealID,
            ...(!userDetails.SentBackToClient && {
              comment: `Client comment: ${values.letterFeedback || ""}`,
            }),
            wpLetterUrl: `${baseUrl}/internal-review?customer=${dealAndToken}`,
            stage: "trainee",
            commentToClient: finalText,
          };

          const updateField = {
            wpLetterReview: true,
            userComments: values.letterFeedback,
            SentBackToClient: false,
            ...(values.clientAnswer && {
              ClientAnswered: true,
              comments: [
                ...(userDetails.comments || []),
                {
                  comment: values.clientAnswer,
                  timestamp: new Date().toISOString(),
                  type: "Client",
                },
              ],
            }),
          };
          const rotares = await rotaGateway({
            method: 'POST',
            path: 'v1/submit-letter',
            body: payload,
          });
          if(rotares.status !== 200){
              throw new Error('ROTA PD API error. Please try again.');
          }
          const updatedData = await updateUser(authToken, updateField);
          if(updatedData.status !== 200){
              throw new Error('StrapiAPI error. Please try again.');
          }
          setUserDetails(updatedData.data);
          if(userDetails.comments === null || userDetails.comments.length === 0){
              navigate("/submitted-successfully");
          }
        } catch (error) {
          handleError(error, 'submit', "Error submitting the letter (Submit comments action)");
        } finally {
          setIsSubmitting(false);
        }
      }
    },
  });

  useEffect(() => {
    if (
      userDetails.userComments !== undefined ||
      userDetails.ClientAnswer !== undefined
    ) {
      formik.setValues({
        letterFeedback: userDetails.userComments || "",
        clientAnswer: userDetails.ClientAnswer || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  const formatAndHighlightContent = (content) => {
    if (!content) return "";

    // Replace text inside square brackets, excluding numeric-only content
    return content.replace(
      /\[(?!\d+\])([^\]]+)\]/g, // Matches non-numeric text inside brackets
      '<span class="highlight">[$1]</span>'
    );
  };

  return (
    <div className="letter client-review">
      <div className="letter-header-message">
        {!isLetterEdit && (
          <div className="letter-header">
            <p>
              Thank you for answering those questions, please see your draft
              letter below
            </p>
            This was created using our advanced AI but don’t worry our lawyers
            will review this before sending it out
          </div>
        )}
        {isLetterEdit && (
          <div className="letter-header">
            <p>Please Edit Your Draft Letter</p>
            See questions in yellow
          </div>
        )}
      </div>

      <div className="letter-container">
        <div className="letter-body">
          {isLoading ? (
            <Loader text={"Loading letter, please wait..."} />
          ) : (
            <>
              <div>
                <div className="letter-content-container">
                  <div id="content" className="letter-content logged-in">
                    <ReactMarkdown rehypePlugins={[rehypeRaw]} skipHtml={false}>
                      {userDetails.checkedWpLetter &&
                        formatAndHighlightContent(
                          userDetails?.checkedWpLetter
                        ).replace(/<br\s*\/?>/gi, "")}
                    </ReactMarkdown>
                  </div>
                  {!isLetterEdit && !isLetterSubmitted && (
                    <div className="letter-actions">
                      {userDetails.userComments
                        ? <>
                            <div
                              className={`check-letter edit ${
                                isSubmitting ? "disabled" : ""
                              }`} // Disable edit button
                              onClick={!isSubmitting ? edit : null} // Disable edit click if submitting
                            >
                              <img src={Edit} alt="check-letter-icon" /> Edit
                            </div>
                            <div
                              className={`send-email approve ${
                                isSubmitting ? "disabled" : ""
                              }`} // Disable approve button
                              onClick={!isSubmitting ? approve : null} // Disable approve click if submitting
                            >
                              <img src={Circle} alt="email-icon" />
                              Approve
                            </div>
                          </>
                        : <>
                          {/* If the user didn't submit any comments yet, hide "Approve" button and show "Edit" button as "Next" action. */}
                          <div
                            className={`check-letter next ${
                              isSubmitting ? "disabled" : ""
                            }`} // Disable edit button
                            onClick={!isSubmitting ? edit : null} // Disable edit click if submitting
                          >
                            <img src={Arrow} alt="check-letter-next-icon" /> Next
                          </div>
                        </>
                      }
                    </div>
                  )}
                </div>
                {(isLetterEdit || isLetterSubmitted) && (
                  <div className="comment">
                    <form onSubmit={formik.handleSubmit}>
                      {!userDetails.SentBackToClient && (
                        <>
                          <label htmlFor="letterFeedback">
                          Please try to answer some of the yellow questions here.
                          If you have already written a timeline of events, you can copy paste it here too:
                          </label>
                          <textarea
                            id="letterFeedback"
                            name="letterFeedback"
                            maxLength={maxCharLimit}
                            onChange={(e) => {
                              formik.handleChange(e);
                              setCharCount(e.target.value.length);
                            }}
                            value={formik.values.letterFeedback}
                          />
                          {formik.errors.letterFeedback && (
                            <div className="error-message">
                              {formik.errors.letterFeedback}
                            </div>
                          )}
                          <span>
                            {charCount}/{maxCharLimit}
                          </span>
                        </>
                      )}
                      <div className="additional-fields">
                        {/* Chat UI for Comments */}
                        {userDetails.comments &&
                          userDetails.comments.length > 0 && (
                            <div className="comments-chat">
                              <h6>Comments History</h6>
                              <div className="comments-list">
                                {userDetails.comments.map(
                                  (comment, index) => (
                                    <div
                                      key={index}
                                      className={`comment-item ${
                                        comment.type === "Internal"
                                          ? "left-align"
                                          : "right-align"
                                      }`}
                                    >
                                      <p className="comment-type">
                                        {comment.type === "Internal"
                                          ? "Lawyer"
                                          : "Me"}{" "}
                                        <span className="comment-timestamp">
                                          {new Date(
                                            comment.timestamp
                                          ).toLocaleString()}
                                        </span>
                                      </p>
                                      <div className="commentHtml"
                                        dangerouslySetInnerHTML={{
                                          __html: commentBeautify(
                                            comment.comment ?? "[no comment]"
                                          ),
                                        }}
                                      />
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                         {userDetails?.comments?.length > 0 && (
                            <>
                              <label htmlFor="clientAnswer">Your Response</label>
                              <textarea
                                id="clientAnswer"
                                name="clientAnswer"
                                maxLength={maxCharLimit}
                                // disabled={userDetails.ClientAnswered}
                                value={formik.values.clientAnswer}
                                onChange={(e) => {
                                  formik.handleChange(e);
                                  setCharCount(e.target.value.length);
                                }}
                              />
                              {formik.errors.clientAnswer && (
                                <div className="error-message">
                                  {formik.errors.clientAnswer}
                                </div>
                              )}
                            </>
                          )}
                      </div>
                      <button
                        type="button"
                        onClick={() => formik.handleSubmit()}
                      >
                        Submit comments
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {error && (
        <div className="clientError">
          <div className="error-message-container">
            <p><strong>There was an error when submitting your comment</strong>, it is on us not on you.</p>
            <p>Please try again later or reach us directly at <a href="tel:communications@monacosolicitors.co.uk">communications@monacosolicitors.co.uk</a></p>
          </div>
        </div>
      )}
      {isSubmitting && (
        <div className="submit-loader">
          <div>
            <img src={Cycle} width="auto" height="auto" alt="cycle" />
            <p>Submitting</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReviewLetter;
